<template>
  <WdsStack gap-sm direction-column>
    <WdsSelect
      v-model="selectedStatus"
      can-deselect
      :label="meta.filter.quickFilter ? '' : $t('Compliance status')"
      :list="statusOptions"
      :placeholder="$t('Any status')"
      class="tw-min-w-[180px]"
      data-test="verified-recruiter-review-status"
      :max-labels-visible="1"
      labels-primary
    />
    <WdsSelect
      v-model="selectedUpdatedBy"
      v-model:search="searchStr"
      :class="meta.filter.quickFilter ? 'tw-min-w-[180px]' : 'tw-min-w-[250px]'"
      :label="meta.filter.quickFilter ? '' : $t('Reviewed by')"
      :list="userOptions"
      :placeholder="$t('Any user')"
      :max-labels-visible="meta.filter.quickFilter ? 1 : 5"
      :load-more-distance="10"
      data-test="verified-recruiter-review-updated-by"
      multi-select
      searchable
      custom-search
      can-deselect
      labels-primary
      persist-input-on-select
      @load-more="loadMore"
    />
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed, ModelRef, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsSelect, WdsStack } from '@worksome/wds-components'
import { graphql } from '@/gql'
import { RecruiterComplianceStatus } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'

defineProps<{ meta: FilterMetaDataProp }>()

type ReviewFilter = {
  status: RecruiterComplianceStatus | null
  updatedBy: string[]
}

const model: ModelRef<ReviewFilter | null> = defineModel({ default: () => null })

const statusOptions = [
  { label: $t('Incomplete'), value: RecruiterComplianceStatus.Incomplete },
  { label: $t('Ready to review'), value: RecruiterComplianceStatus.ReadyToReview },
  { label: $t('Changes requested'), value: RecruiterComplianceStatus.ChangesRequested },
  { label: $t('Cleared'), value: RecruiterComplianceStatus.Cleared },
]

const searchStr = ref('')

const { result, loading, fetchMore } = useQuery(
  graphql(`
    query VerifiedRecruiterReviewFilterUsers(
      $companyId: ID!
      $search: String
      $first: Int!
      $page: Int
    ) {
      company(id: $companyId) {
        id
        teamMembersAndOwners(
          search: $search
          first: $first
          page: $page
          permission: "manage-compliance-tracker"
        ) {
          data {
            id
            name
          }
          paginatorInfo {
            hasMorePages
            currentPage
          }
        }
      }
    }
  `),
  () => ({
    companyId: currentAccount.globalId,
    search: searchStr.value,
    first: 25,
    page: 1,
  }),
  { clientId: 'publicApi' },
)

const userOptions = computed<{ label: string; value: string }[]>(() =>
  (result.value?.company?.teamMembersAndOwners?.data ?? []).map((user) => ({
    value: user.id,
    label: user.name,
  })),
)
function writeModel(patch: Partial<ReviewFilter>) {
  const next: ReviewFilter = {
    status: model.value?.status ?? null,
    updatedBy: model.value?.updatedBy ?? [],
    ...patch,
  }
  // Collapse to null when both facets are empty so the filter pill clears itself.
  model.value = next.status === null && next.updatedBy.length === 0 ? null : next
}

// Read/write the {label, value} shape WdsSelect emits, but persist only the bare
// enum/id values into the GraphQL variable so the resolver gets a valid input.
const selectedStatus = computed({
  get: () => statusOptions.find((o) => o.value === model.value?.status) ?? null,
  set: (option) => {
    writeModel({ status: (option?.value as RecruiterComplianceStatus) ?? null })
  },
})

const selectedUpdatedBy = computed({
  get: () =>
    (model.value?.updatedBy ?? [])
      .map((id) => userOptions.value.find((o) => o.value === id))
      .filter((o): o is { label: string; value: string } => o !== undefined),
  set: (options: { label: string; value: string }[]) => {
    writeModel({ updatedBy: options.map((o) => o.value) })
  },
})

function loadMore() {
  if (loading.value) return
  const info = result.value?.company?.teamMembersAndOwners?.paginatorInfo
  if (!info?.hasMorePages) return
  fetchMore({ variables: { page: info.currentPage + 1 } })
}
</script>
